// Last place: the working page a person was most recently on, remembered per
// device as a Workspace and a section. Entering the product with no explicit
// destination (`/`, `/<org>`) returns them there; an explicit URL always wins.
// Settings and admin pages are never a Last place. Memory is a hint,
// not an authority: when it cannot be honoured it is forgotten and the default
// applies, so entry can never loop. See CONTEXT.md.

import { launchLinkFor } from "./launch-place";

export const SECTIONS = ["media-performance", "launch", "review", "audit"] as const;
export type Section = (typeof SECTIONS)[number];

export interface LastPlace {
  orgSlug: string;
  /** null when the workspace is remembered but no working section is. */
  section: Section | null;
}

type PlaceStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const LAST_ORG_KEY = "toolkit:last-org";
const sectionKey = (orgSlug: string) => `toolkit:last-section:v1:${orgSlug}`;

const browserStorage = (): PlaceStorage | undefined =>
  typeof window === "undefined" ? undefined : window.localStorage;

const isSection = (value: unknown): value is Section =>
  typeof value === "string" && (SECTIONS as readonly string[]).includes(value);

/** The working section a pathname under a workspace belongs to, or null. */
export function sectionFromPathname(orgSlug: string, pathname: string): Section | null {
  const prefix = `/${orgSlug}/`;
  if (!pathname.startsWith(prefix)) return null;
  const head = pathname.slice(prefix.length).split("/")[0];
  return isSection(head) ? head : null;
}

/** The remembered working section of one workspace, or null. */
export function readSection(orgSlug: string, storage = browserStorage()): Section | null {
  try {
    const section = storage?.getItem(sectionKey(orgSlug));
    return isSection(section) ? section : null;
  } catch {
    return null;
  }
}

/** The remembered workspace and its section, or null when nothing is remembered. */
export function readLastPlace(storage = browserStorage()): LastPlace | null {
  try {
    const orgSlug = storage?.getItem(LAST_ORG_KEY);
    return orgSlug ? { orgSlug, section: readSection(orgSlug, storage) } : null;
  } catch {
    return null;
  }
}

/**
 * Records a working page as the Last place. A page that is not a working
 * section (settings) leaves the previous Last place untouched, even in
 * another workspace.
 */
export function rememberLastPlace(
  place: { orgSlug: string; section: Section | null },
  storage = browserStorage(),
): void {
  if (!place.section) return;
  try {
    storage?.setItem(LAST_ORG_KEY, place.orgSlug);
    storage?.setItem(sectionKey(place.orgSlug), place.section);
  } catch {
    // Private browsing or a full device never blocks the product itself.
  }
}

/** Drops a workspace as a hint: after this, entry cannot pick it again. */
export function forgetWorkspace(orgSlug: string, storage = browserStorage()): void {
  try {
    if (storage?.getItem(LAST_ORG_KEY) === orgSlug) storage.removeItem(LAST_ORG_KEY);
    storage?.removeItem(sectionKey(orgSlug));
  } catch {
    // Nothing to forget when storage is unavailable.
  }
}

interface EntryViewer {
  organizations: ReadonlyArray<{ slug: string }>;
  isSuperAdmin: boolean;
  isBrandManager?: boolean;
}

/**
 * Where a bare `/` lands: the remembered workspace when still accessible,
 * else the first workspace, else `/admin` for a super admin or brand manager.
 * Null means the person has no workspace at all.
 */
export function entryTarget(viewer: EntryViewer, storage = browserStorage()) {
  const remembered = readLastPlace(storage)?.orgSlug;
  const target =
    viewer.organizations.find((org) => org.slug === remembered) ?? viewer.organizations[0];
  if (target) return { to: "/$orgSlug" as const, params: { orgSlug: target.slug } };
  if (viewer.isSuperAdmin || viewer.isBrandManager) return { to: "/admin" as const };
  return null;
}

/** Where a bare `/<org>` lands: its remembered section, defaulting to Media Performance. */
export function sectionTarget(orgSlug: string, storage = browserStorage()) {
  const section = readSection(orgSlug, storage) ?? "media-performance";
  const params = { orgSlug };
  switch (section) {
    case "launch":
      return launchLinkFor(orgSlug, storage);
    case "review":
      return { to: "/$orgSlug/review" as const, params };
    case "audit":
      return { to: "/$orgSlug/audit" as const, params };
    default:
      return { to: "/$orgSlug/media-performance" as const, params };
  }
}
